import { faGrid2, faHotel } from "@fortawesome/pro-light-svg-icons";
import { faGrid2 as faGrid2Solid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Tooltip, Typography } from "@mui/material";
import { memo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";

import type { Asset } from "@/Dashboard/pages/AssetLibrary/types";
import AccessControl from "@/components/AccessControl/AccessControl";
import { MEDIA_SIZE, PROPERTY_TIERS } from "@/constants";
import { useTabStatus, useUrlParams } from "@/hooks";
import { CircleChecked, CircleUnchecked, ExpMenu } from "@/icons";
import {
  addCurrentSelectedItems,
  resetCurrentSelectedItems,
  setSelectAll,
  setSimpleValue,
  updateVisualBuilderData,
} from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import {
  currentExperienceSelector,
  currentMediaItemSelector,
  currentPropertySelector,
  currentSelectedItemsSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";
import type { User } from "@/store/v2/currentUser/selectors";
import { getMediaLink } from "@/utils/mediaLink";
import { getInitialsFromName } from "@/utils/user-utils";

import * as S from "./styled";
import Elements from "../Elements/Elements";
import ContactDialog from "../Header/ContactDialog";
import ContactDialogV2 from "../Header/ContactDialogV2";
import PropertyInfoCard from "../Header/PropertyInfoCard/PropertyInfoCard";

/**
 * Props for the ExperienceToolbar component
 */
interface ExperienceToolbarProps {
  /** All available element data */
  readonly allElementData: unknown[];
  /** Filtered media assets */
  readonly filteredMediaItems: Asset[];
  /** Grid view state */
  readonly gridView: boolean;
  /** Left drawer state */
  readonly leftDrawer: boolean;
  /** Left drawer auto-hide setting */
  readonly leftDrawerAutoHide: boolean;
  /** Toolbar lock state */
  readonly locked: boolean;
}

/**
 * ExperienceToolbar provides navigation and control elements for the experience builder
 */
function ExperienceToolbar(props: ExperienceToolbarProps) {
  const { locked, gridView, leftDrawer, leftDrawerAutoHide, filteredMediaItems, allElementData } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const dispatch = useThunkDispatch();

  const { mediaItem: currentMediaItem } = useSelector(currentMediaItemSelector);

  const { selectAll } = useSelector(currentSelectedItemsSelector);

  const params = useUrlParams();
  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { createdBy: createdByData, flags } = visualBuilderData;

  const createdBy = createdByData as User | undefined;

  const { experience } = useSelector(currentExperienceSelector);
  const { dbcEnabled, dbcUser, propertyInfoCardEnabled = true } = experience;
  const currentDbcUser = dbcUser ?? createdBy;

  const photo = getMediaLink({ size: MEDIA_SIZE.tiny, link: currentDbcUser?.photoUrl });

  const nameInitials = getInitialsFromName(currentDbcUser?.name || "");

  const { hasTabStatus, isTabPresent, isTabCustomize, isTabPublic, isTabPublicSingleShare, isTabPublicMediaShare } =
    useTabStatus();

  const { property } = useSelector(currentPropertySelector);

  const { name: propertyName } = property;
  const experienceAvatars = property?.customSettings?.experienceAvatars || true;
  const isOrganizationsSection = location.pathname.startsWith("/organizations");

  const { tier } = property;
  const tierTitle = tier?.title;

  const hasCorrectPropertyTier = [PROPERTY_TIERS.PRO, PROPERTY_TIERS.STANDARD].includes(
    tierTitle as typeof PROPERTY_TIERS.PRO | typeof PROPERTY_TIERS.STANDARD,
  );

  /** Determines visibility of user icon based on status and tier */
  const showUserIcon = dbcEnabled && hasTabStatus && isTabPublic && hasCorrectPropertyTier && experienceAvatars;
  const showPropertyInfoIcon =
    (isTabPublicSingleShare || isTabPublicMediaShare || isTabPublic) && propertyInfoCardEnabled;

  /** Toggles left drawer visibility */
  const toggleDrawerOpen = () => {
    if (locked && isTabCustomize) return;
    void dispatch(updateVisualBuilderData(params, { leftDrawer: !leftDrawer }));
  };

  /** Toggles between grid and list view */
  const toggleGridView = () => {
    if (locked && isTabCustomize) return;

    void dispatch(
      updateVisualBuilderData(params, {
        gridView: !gridView,
        currentMediaItemId: gridView ? filteredMediaItems[0]?.id : null,
      }),
    );
  };

  /** Handles select all functionality */
  const toggleSelectAll = () => {
    if (selectAll) {
      dispatch(resetCurrentSelectedItems());
    } else {
      dispatch(addCurrentSelectedItems(filteredMediaItems.reduce((a, v) => ({ ...a, [v.id]: v }), {})));
    }
    dispatch(setSelectAll(!selectAll));
  };

  /** Opens contact dialog */
  const handleUserClick = () => {
    dispatch(setSimpleValue("contact_salesperson", true));
  };

  /** Opens property info dialog */
  const handlePropertyInfoClick = () => {
    dispatch(setSimpleValue("property_info_card", true));
  };

  return (
    <S.ToolbarContainer $isTabPresent={isTabPresent}>
      {!leftDrawerAutoHide && !isTabPublicSingleShare && (
        <S.BigIconButton
          data-testid="drawer-button"
          aria-label="open drawer"
          color="inherit"
          edge="start"
          onClick={toggleDrawerOpen}
          size="large"
          data-analytics="vb-experience-menu-toggle"
        >
          <ExpMenu />
        </S.BigIconButton>
      )}

      <S.InnerMenuContainer
        $isPublicSingleShare={isTabPublicSingleShare}
        $isMobile={isMobile}
        $leftDrawer={leftDrawer}
        $leftDrawerAutoHide={leftDrawerAutoHide}
        data-testid="inner-menu"
      >
        <S.SmallIconButton
          data-testid="grid-view-button"
          onClick={toggleGridView}
          size="large"
          name="Grid toggle button"
          aria-label="Grid layout"
        >
          <FontAwesomeIcon icon={gridView ? faGrid2Solid : faGrid2} color="white" style={{ fontSize: "16px" }} />
        </S.SmallIconButton>

        {gridView && hasTabStatus && !(isTabPublicSingleShare || isTabPublic) ? (
          <AccessControl
            allowedPermissions={["experiences:create"]}
            allowedTiers={isOrganizationsSection ? [] : [PROPERTY_TIERS.PRO]}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                data-testid="select-all-button"
                onClick={locked ? undefined : toggleSelectAll}
                component="span"
                sx={{ display: "flex", cursor: "pointer", gap: "5px", pointerEvents: "auto" }}
              >
                {selectAll ? <CircleChecked /> : <CircleUnchecked />}
                <Typography>Select All</Typography>
              </Box>
            </Box>
          </AccessControl>
        ) : (
          <S.TitleContainer>
            <Typography variant="body1" sx={{ fontSize: "14px", fontWeight: "bold" }}>
              {propertyName}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "14px" }}>
              {filteredMediaItems.length ? currentMediaItem.name : ""}
            </Typography>
          </S.TitleContainer>
        )}

        {!gridView && (
          <S.AllElementsContainer>
            <Elements anchorEl={anchorEl} setAnchorEl={setAnchorEl} elements={allElementData} />
          </S.AllElementsContainer>
        )}

        {showPropertyInfoIcon && (
          <S.SmallIconButton
            data-testid="property-info-button"
            onClick={handlePropertyInfoClick}
            size="large"
            name="Property info button"
            aria-label="Property info"
          >
            <FontAwesomeIcon icon={faHotel} color="white" style={{ fontSize: "20px" }} />
          </S.SmallIconButton>
        )}

        {showUserIcon && (
          <>
            <Tooltip arrow title="Contact">
              <S.SmallIconButton
                data-testid="user-button"
                onClick={handleUserClick}
                size="large"
                name="User info button"
                aria-label="User info"
              >
                {photo ? <Avatar src={photo} /> : nameInitials}
              </S.SmallIconButton>
            </Tooltip>
          </>
        )}
      </S.InnerMenuContainer>

      {flags?.["digital-business-card"] ? (
        <ContactDialogV2 dbcContact={currentDbcUser} />
      ) : (
        <ContactDialog documentName="salesperson" createdBy={createdBy} />
      )}

      <PropertyInfoCard />
    </S.ToolbarContainer>
  );
}

export default memo(ExperienceToolbar);
